<template>
  <!-- Payment Information -->
  <v-container class="pa-0">
    <v-card flat rounded="0">
      <!-- Title -->
      <v-card-title mt-4 mb-4 class="marginAuto topBottomSpace">
        <v-row>
          <v-col>
            <h1 role="heading" aria-level="2" class="card-title text-h2">
              {{ $t('L_policy_payment_information') }}
            </h1>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- payment currently being processed -->
      <OnexAlert class="mb-4" :type="Notifications.info" v-if="showPayInProgressBanner">
        {{ $t('L_important_there_is_a_payment_in_process') }}
      </OnexAlert>

      <!-- Payment in Process -->
      <MessageDialog
        :show-dialog="showMessageDialog"
        :has-button-two="false"
        type="warning"
        :title="$t('L_payment_in_process')"
        :message="$t('L_a_payment_is_currently_being_processed')"
        :button-one-label="$t('L_close')"
        :is-persistent="true"
        @buttonOneAction="showMessageDialog = false"
      />

      <!-- Pay Now Banner -->
      <OnexAlert :type="Notifications[paynowType]" icon v-if="showPayNowMsg">
        <template #default><span v-html="payNowLinkDesc"></span></template>
        <template #cta>
          <OnexButton @click="payNow" color="primary" :prepend-icon="mdiCurrencyUsd">
            {{ $t('L_pay_now') }}
          </OnexButton>
        </template>
      </OnexAlert>

      <v-divider v-if="!showPayInProgressBanner && !showPayNowInfoCard" />

      <v-card-text
        v-if="!editPaymentMethod && !updatingBank && paymentInfoShowPaymentAmountDateBillingMethod"
      >
        <v-container>
          <v-row justify="center" class="text-center">
            <!-- * Amount -->
            <v-col cols="12" sm="6" md="3" class="mt-4">
              <!-- total premium -->
              <h4 class="text-h5">{{ $t('L_policy_total_premium') }}</h4>
              <!-- hasAnnuity -->
              <template v-if="hasAnnuity">
                <h3 class="text-h3 font-weight-regular mt-2">{{ formattedTotalPremium }}</h3>
                <h6 class="text-h6 font-weight-regular mt-2">
                  <span class="font-weight-medium">{{ $t('L_life_insurance_premium') }}</span>
                  - <span>{{ formattedInsurancePremium }}</span>
                </h6>
                <h6 class="text-h6 font-weight-regular mt-2">
                  <span class="font-weight-medium">{{ getPolicyAnnuityLabel }}</span>
                  - <span>{{ formattedAnnuityPremium }}</span>
                </h6>
              </template>
              <!-- not hasAnnuity -->
              <template v-else>
                <h3 class="text-h4 font-weight-regular mt-2">{{ formattedInsurancePremium }}</h3>
              </template>
            </v-col>
            <!-- * Draft Date -->
            <v-col cols="12" sm="6" md="3" class="mt-4">
              <!-- policy.nextDraftDate && policy.billingForm !== 'H1' -->
              <template v-if="policy.nextDraftDate && policy.billingForm !== 'H1'">
                <h4 class="text-h5">{{ formattedNextDate }}</h4>
                <h3 class="text-h4 font-weight-regular mt-2">
                  {{ formattedDraftDate }}
                  <a
                    v-if="displayEditFeatureFlag"
                    id="edit_billing_method"
                    class="blueLinkPointer ml-2"
                    href="javascript:void(0)"
                    :aria-label="$t('L_edit') + ' withdrawal date'"
                    @click="callWithDrawalDateEdit"
                    >{{ $t('L_edit') }}
                  </a>
                </h3>
              </template>
              <!-- policy.nextPaymentDueDate && policy.billingForm !== 'H1' -->
              <template v-else-if="policy.nextPaymentDueDate && policy.billingForm !== 'H1'">
                <h4 class="text-h4">{{ formattedNextDate }}</h4>
                <h3 class="text-h3 font-weight-regular mt-2">{{ formattedPaymentDueDate }}</h3>
              </template>
              <!-- policy.billingForm === 'H1' -->
              <template v-else-if="policy.billingForm === 'H1'">
                <h4 class="text-h4">{{ $t(dateLabel) }}</h4>
                <h3 class="text-h3 font-weight-regular mt-2">{{ formattedPaidToDate }}</h3>
              </template>
            </v-col>
            <!-- * Payment Method -->
            <v-col cols="12" sm="6" md="3" class="mt-4">
              <h4 class="text-h5">{{ $t(paymentLabel) }}</h4>
              <h3 class="text-h4 font-weight-regular mt-2">{{ $t(paymentMethod) }}</h3>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <!-- View Payment Details -->
      <ViewPaymentDetails
        v-if="allowExpandDetails || showPayNowInfoCard"
        :pay-now="showPayNowInfoCard"
      />
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { currency, formatDate, toUTCDate } from '@/shared/util/formatters';
import stringValues from '@/shared/util/stringValues';
import PolicyDetailsInfo from '../PolicyDetailsInfo.vue';
// import PolicyBenefits from '../PolicyBenefits/PolicyBenefits.vue';
import ViewPaymentDetails from './PaymentInformationPaymentDetails.vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import DynatraceClient from '@/plugins/dynatracePlugin';
import MessageDialog from '@/components/MessageDialog.vue';
import { mdiAlert, mdiInformationOutline, mdiCurrencyUsd } from '@mdi/js';
import { OnexAlert, Notifications, OnexButton } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'PaymentInformationCard',
  components: {
    PolicyDetailsInfo,
    ViewPaymentDetails,
    MessageDialog,
    OnexAlert,
    OnexButton,
  },
  data: () => ({
    paynowType: 'info',
    showMessageDialog: false,
    mdiInformationOutline,
    mdiAlert,
    mdiCurrencyUsd,
    Notifications,
  }),
  computed: {
    ...mapStores(usePolicyStore, useGlobalStore),
    ...mapState(useGlobalStore, {
      currentLocale: 'getLocale',
      dateFormat: 'getDateFormat',
    }),
    ...mapState(useGlobalStore, ['containsFeature']),
    ...mapState(usePolicyStore, {
      policy: 'currentPolicy',
      updatingBank: 'updatingBank',
      editPaymentMethod: 'editPaymentMethod',
      withDrawalDateStep: 'withDrawalDateStep',
      paymentStep: 'paymentStep',
      selectedPolicyIndex: 'selectedPolicyIndex',
      showPayNowInfoCard: 'showPayNowInfoCard',
      paymentInfoShowPaymentAmountDateBillingMethod:
        'paymentInfoShowPaymentAmountDateBillingMethod', // TODO: access state directly
      policies: 'policies',
    }),
    icon() {
      if (this.type === 'warning') {
        return mdiAlert;
      } else if (this.type === 'info') {
        return mdiInformationOutline;
      }
    },
    getPolicyAnnuityLabel() {
      if (this.policyStore.currentPolicy != null) {
        if (this.currentLocale === 'fr_CA') {
          return (
            this.policyStore.currentPolicy?.annuity[0]?.planDescriptionFrench + this.$t('L_payment')
          );
        }
        return this.policyStore.currentPolicy?.annuity[0]?.planDescription + this.$t('L_payment');
      }
      return this.$t('L_payment');
    },
    hasAnnuity() {
      return this?.policyStore.currentPolicy?.paynowInfo?.hasAnnuity;
    },
    showPayNowMsg() {
      return this.policyStore.showPayNow && !this.showPayInProgressBanner;
    },
    formattedDraftDate() {
      return formatDate(this.policy?.nextDraftDate, this.dateFormat);
    },
    formattedPaymentDueDate() {
      const formattedDate = formatDate(
        this.policyStore.currentPolicy?.nextPaymentDueDate,
        this.globalStore.getDateFormat
      );
      return formattedDate;
    },
    formattedTotalPremium() {
      return currency(
        `${
          (+this.policyStore.currentPolicy?.modePremium || 0) +
          (+this.policyStore.currentPolicy?.data?.annuityModePrem || 0)
        }`,
        2
      );
    },
    formattedAnnuityPremium() {
      return this?.policyStore.currentPolicy?.data?.annuityModePrem
        ? currency(this.policyStore.currentPolicy.data?.annuityModePrem, 2)
        : '';
    },
    formattedInsurancePremium() {
      if (this.policyStore.currentPolicy?.paynowInfo?.hasAnnuity) {
        return currency(this.policyStore.currentPolicy?.data?.modePremium, 2);
      }
      if (
        this.policyStore.currentPolicy?.paynowInfo?.totalPaymentAmount ||
        this.policyStore.currentPolicy?.paynowInfo?.totalPaymentAmount === 0
      ) {
        return currency(+this.policyStore.currentPolicy?.paynowInfo?.totalPaymentAmount, 2);
      }
      return '';
    },
    formattedPaidToDate() {
      return formatDate(
        this.policyStore.currentPolicy?.paymentInfo?.PaidToDate,
        this.globalStore.getDateFormat
      );
    },
    formattedNextDate() {
      return this.policyStore.currentPolicy?.billingForm
        ? this.$t(stringValues.nextDate(this.policyStore.currentPolicy))
        : '';
    },
    dateLabel() {
      if (this.policyStore.currentPolicy?.billingForm === 'H1') {
        return 'L_policy_paid_to_date_H1';
      }
      if (
        this.policyStore.currentPolicy?.billingMode === '01' &&
        this.policyStore.currentPolicy?.billingForm === 'B2'
      ) {
        return 'L_policy_next_auto_draft';
      }
      return 'L_policy_next_premium';
    },
    paymentLabel() {
      switch (this.policyStore.currentPolicy?.billingForm) {
        case 'B2':
        case 'C2':
        case 'D2':
          return 'L_policy_payment_method';
        case 'A1':
        case 'H1':
        default:
          return 'L_policy_billing_method';
      }
    },
    paymentMethod() {
      return stringValues.paymentMethod(this.policyStore.currentPolicy);
    },
    allowExpandDetails() {
      const { billingForm } = this.policyStore.currentPolicy;
      return billingForm !== 'D2' && billingForm !== 'C2' && billingForm !== 'H1';
    },
    payInProgress() {
      return this.policyStore.currentPolicy.paymentInfo.payInProcess;
    },
    kubraPaymentInProgress() {
      // return this.policyStore.currentPolicy?.kubraPaymentStatus !== null;
      return (
        this.policyStore.currentPolicy?.kubraPaymentStatus !== null &&
        this.policyStore.currentPolicy?.kubraPaymentStatus !== undefined
      );
    },
    showPayInProgressBanner() {
      return this.payInProgress || this.kubraPaymentInProgress;
    },
    isCanadianPolicy() {
      return !!this.policyStore.currentPolicy?.isCanadaPolicy;
    },
    payNowLinkDesc() {
      let payNowLinkDesc = this.policyStore.currentPolicy?.payNowLinkDesc || '';
      const paidToDate = this.policyStore.currentPolicy?.paymentInfo?.PaidToDate;
      const billForm = this.policyStore.currentPolicy?.billingForm;
      const DIRECT_BILL = billForm?.toLowerCase() === 'A1'.toLowerCase();
      const paidDateUTC = toUTCDate(paidToDate);
      const todayUTC = toUTCDate(new Date());
      const isPaidToDateBeforeToday = isBefore(paidDateUTC, todayUTC);
      const isPaidToDateAfterToday = isAfter(paidDateUTC, todayUTC);
      const isPaidToDateToday = isSameDay(paidDateUTC, todayUTC);
      const paidToDateFormatted = this.formattedPaidToDate;
      const paymentAmt = this.policyStore.currentPolicy?.paynowInfo?.totalPaymentAmount;

      this.paynowType = 'info';

      if (this.policyStore.currentPolicy.paynowInfo?.inSufficientFunds) {
        this.paynowType = 'warning';
        payNowLinkDesc = this.$t('L_your_automatic_payment_of', { amount: paymentAmt });
      } else if (DIRECT_BILL) {
        if (paidToDate && isPaidToDateBeforeToday) {
          this.paynowType = 'warning';
          payNowLinkDesc = this.$t('L_a_payment_was_due_on', {
            amount: paymentAmt,
            date: paidToDateFormatted,
          });
        } else if (paidToDate && isPaidToDateAfterToday) {
          payNowLinkDesc = this.$t('L_a_payment_is_due_on', {
            amount: paymentAmt,
            date: paidToDateFormatted,
          });
        } else if (paidToDate && isPaidToDateToday) {
          payNowLinkDesc = this.$t('L_a_payment_is_due_today', { amount: paymentAmt });
        }
        if (this.policyStore.currentPolicy.paynowInfo?.hasMultipleOptions) {
          payNowLinkDesc += ` ${this.$t('L_a_payment_option_minimum', {
            amount: this.policyStore.currentPolicy.paynowInfo.minimumPaymentAmount,
          })}`;
        }
      } else {
        payNowLinkDesc = '';
      }
      return payNowLinkDesc;
    },
    displayEditFeatureFlag() {
      return this.containsFeature('DRAFT-DATE');
    },
  },
  methods: {
    ...mapActions(usePolicyStore, {
      setWithDrawalDateStep: 'setWithDrawalDateStep',
    }),
    callWithDrawalDateEdit() {
      try {
        const i = DynatraceClient.enterAction('ChangeWithdrawalDay_start');
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.setWithDrawalDateStep(1);
      this.$router.push({
        name: RouteNames.WITHDRAWALDATESTEP,
        params: { contractId: this.indexPolicy },
      });
      try {
        const i = DynatraceClient.enterAction('SelectWithdrawalDay_PageDisplayed');
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    },
    async payNow() {
      await this.policyStore.getPaymentInfo(this.policyStore.currentPolicy.contractId);
      if (this.showPayNowMsg) {
        // * Dynatrace Tag (https://primerica.atlassian.net/wiki/spaces/PD/pages/19955873/Payments+-+Custom+Dynatrace+Tags)
        try {
          const i =
            /* import DynatraceClient from '@/plugins/dynatracePlugin';*/ DynatraceClient.enterAction(
              `PolicyDetails_payNow_policy_${
                this.policies[0]?.contractId || this.selectedPolicyIndex
              }-${this.policies[0]?.insuredName}_Selected`
            );
          /* import DynatraceClient from '@/plugins/dynatracePlugin';*/ DynatraceClient.leaveAction(
            i
          );
        } catch (_error) {
          /* empty */
        }
        try {
          const i =
            /* import DynatraceClient from '@/plugins/dynatracePlugin';*/ DynatraceClient.enterAction(
              'OneTimePayment_start'
            );
          /* import DynatraceClient from '@/plugins/dynatracePlugin';*/ DynatraceClient.leaveAction(
            i
          );
        } catch (_error) {
          /* empty */
        }
        this.policyStore.paymentStep = 1;
        this.$router.push({
          name: RouteNames.PAYMENT_STEP,
          params: { contractId: this.selectedPolicyIndex },
        });
      } else {
        this.showMessageDialog = true;
      }
    },
    // TODO: delete if unused
    // togglePanel(currentPanelValue: any) {
    //   if (currentPanelValue === 0) {
    //     this.policyStore.setPanelValue(-1);
    //   } else {
    //     this.showPayNowInfoCard = false;
    //     this.policyStore.setPanelValue(0);
    //   }
    // },
  },
  mounted() {
    this.policyStore.paymentInfoShowPaymentAmountDateBillingMethod = true;
    if (this.policyStore.currentPolicy.showPayNowLink) {
      this.policyStore.showPayNowInfoCard = true;
      if (this.$vuetify.display.lgAndUp) {
        window.scrollTo(0, 650);
      } else if (this.$vuetify.display.md || this.$vuetify.display.sm) {
        window.scrollTo(0, 1050);
      } else {
        window.scrollTo(0, 1450);
      }
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.maxwidthButton {
  width: 300px;
  max-width: 300px !important;
}

.floatright {
  float: right;
}

.infoBox {
  background-color: #e2f0fe;
  border-top: 4px solid #3399ff;
  margin-left: 0px !important;
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
}

.warningBox {
  background-color: #fdf3e2;
  border-top: 4px solid #f39c12;
  margin-left: 0px !important;
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
}

.marginAuto {
  margin: auto;
}

.topBottomSpace {
  padding-top: 16px;
  padding-bottom: 16px;
}

.iconBig {
  font-size: 28px;
}
.blueLinkPointer {
  text-decoration: none;
  color: rgb(12, 109, 202);
  font-weight: 600;
  cursor: pointer;
}
</style>
